<template>
    <div
        v-if="!dismissed"
        class="mt-2 rounded-xl border border-gray-200 bg-white p-4"
        :class="interactive ? 'border-[#FF421E]/30' : 'opacity-70'"
    >
        <div class="flex items-start gap-2">
            <QuestionMarkCircleIcon
                class="mt-0.5 h-4 w-4 shrink-0 text-[#FF421E]"
            />
            <p class="text-sm font-medium text-gray-800">
                {{ questions.intro ?? t("vue.ai_chat_questions_title") }}
            </p>
        </div>

        <div class="mt-3 space-y-3">
            <div v-for="question in questions.questions" :key="question.id">
                <p class="text-sm text-gray-700">{{ question.text }}</p>

                <div
                    v-if="question.options.length > 0"
                    class="mt-1.5 flex flex-wrap gap-1.5"
                >
                    <button
                        v-for="option in question.options"
                        :key="option.id"
                        type="button"
                        class="rounded-full border px-3 py-1 text-xs font-medium transition"
                        :class="[
                            selected[question.id] === option.id
                                ? 'border-[#FF421E] bg-[#FF421E] text-white'
                                : 'border-gray-300 text-gray-600',
                            interactive
                                ? 'cursor-pointer'
                                : 'cursor-not-allowed',
                            interactive && selected[question.id] !== option.id
                                ? 'hover:border-[#FF421E] hover:text-[#FF421E]'
                                : '',
                        ]"
                        :aria-pressed="selected[question.id] === option.id"
                        :disabled="!interactive"
                        @click="pick(question.id, option.id)"
                    >
                        {{ option.label }}
                    </button>

                    <button
                        type="button"
                        class="rounded-full border border-dashed px-3 py-1 text-xs font-medium transition"
                        :class="[
                            isOtherOpen(question.id)
                                ? 'border-[#FF421E] text-[#FF421E]'
                                : 'border-gray-300 text-gray-500',
                            interactive
                                ? 'cursor-pointer hover:border-[#FF421E] hover:text-[#FF421E]'
                                : 'cursor-not-allowed',
                        ]"
                        :aria-pressed="isOtherOpen(question.id)"
                        :aria-expanded="isOtherOpen(question.id)"
                        :disabled="!interactive"
                        @click="toggleOther(question.id)"
                    >
                        {{ t("vue.ai_chat_questions_other") }}
                    </button>
                </div>

                <input
                    v-if="
                        question.options.length === 0 ||
                        isOtherOpen(question.id)
                    "
                    v-model="typed[question.id]"
                    type="text"
                    class="mt-1.5 w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm text-gray-800 focus:border-[#FF421E] focus:ring-0 disabled:bg-gray-50"
                    :maxlength="ANSWER_MAX"
                    :placeholder="t('vue.ai_chat_questions_answer_placeholder')"
                    :disabled="!interactive"
                />
            </div>
        </div>

        <div class="mt-3">
            <label
                :for="freeTextId"
                class="text-xs font-medium text-gray-500"
                >{{ t("vue.ai_chat_questions_free_label") }}</label
            >
            <textarea
                :id="freeTextId"
                v-model="freeText"
                rows="2"
                class="mt-1 w-full resize-none rounded-lg border border-gray-200 p-3 text-sm text-gray-800 focus:border-[#FF421E] focus:ring-0 disabled:bg-gray-50"
                :maxlength="FREE_TEXT_MAX"
                :placeholder="t('vue.ai_chat_questions_free_placeholder')"
                :disabled="!interactive"
            />
        </div>

        <div
            v-if="interactive"
            class="mt-3 flex items-center justify-end gap-3"
        >
            <button
                type="button"
                class="cursor-pointer text-xs font-medium text-gray-400 transition hover:text-gray-600"
                @click="skip"
            >
                {{ t("vue.ai_chat_questions_skip") }}
            </button>
            <button
                type="button"
                class="inline-flex cursor-pointer items-center gap-1.5 rounded-full bg-[#FF421E] px-3 py-1.5 text-xs font-medium text-white shadow-sm transition hover:bg-[#E03A1A] active:scale-95 disabled:cursor-not-allowed disabled:opacity-40"
                :disabled="saving || !hasAnything"
                @click="submit"
            >
                <PaperAirplaneIcon class="h-4 w-4 shrink-0" />
                {{ t("vue.ai_chat_questions_send") }}
            </button>
        </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, ref, useId } from "vue";
import axios from "axios";
import route from "ziggy-js";
import {
    PaperAirplaneIcon,
    QuestionMarkCircleIcon,
} from "@heroicons/vue/24/outline";
import type { IAiChatQuestionSet } from "@/types/AiChat/IAiChat";
import { create } from "@/composables/useNotifications";
import { useTranslations } from "@/composables/useTranslations";

const ANSWER_MAX = 300;
const FREE_TEXT_MAX = 1000;
const CONFLICT = 409;

const props = withDefaults(
    defineProps<{
        questions: IAiChatQuestionSet;
        chatId: number;
        messageId: number;
        widget?: boolean;
        disabled?: boolean;
    }>(),
    { widget: false, disabled: false },
);

const emit = defineEmits<{ submitted: [] }>();

const { t } = useTranslations();

const selected = ref<Record<string, string>>({});
const typed = ref<Record<string, string>>({});
const otherOpen = ref<Record<string, boolean>>({});
const freeText = ref("");
const saving = ref(false);
const dismissed = ref(false);
const freeTextId = useId();

const interactive = computed(
    () => props.disabled === false && props.questions.answered === false,
);

const hasAnything = computed(() => buildAnswers().length > 0 || free() !== "");

const free = (): string => freeText.value.trim();

const buildAnswers = (): Array<{
    question_id: string;
    option_id?: string;
    text?: string;
}> => {
    const answers = [];

    for (const question of props.questions.questions) {
        const optionId = selected.value[question.id];
        if (optionId) {
            answers.push({ question_id: question.id, option_id: optionId });
            continue;
        }

        const text = (typed.value[question.id] ?? "").trim();
        if (text !== "") {
            answers.push({ question_id: question.id, text });
        }
    }

    return answers;
};

const isOtherOpen = (questionId: string): boolean =>
    otherOpen.value[questionId] === true;

const pick = (questionId: string, optionId: string): void => {
    if (!interactive.value) {
        return;
    }
    selected.value = {
        ...selected.value,
        [questionId]: selected.value[questionId] === optionId ? "" : optionId,
    };
    otherOpen.value = { ...otherOpen.value, [questionId]: false };
    typed.value = { ...typed.value, [questionId]: "" };
};

const toggleOther = (questionId: string): void => {
    if (!interactive.value) {
        return;
    }
    const open = !isOtherOpen(questionId);
    otherOpen.value = { ...otherOpen.value, [questionId]: open };
    selected.value = { ...selected.value, [questionId]: "" };

    if (open === false) {
        typed.value = { ...typed.value, [questionId]: "" };
    }
};

const skip = (): void => {
    dismissed.value = true;
};

const endpoint = computed(() =>
    props.widget
        ? route("ai-chat.api.messages.answers.store", {
              chat: props.chatId,
              message: props.messageId,
          })
        : route("ai-chat.messages.answers.store", {
              chat: props.chatId,
              message: props.messageId,
          }),
);

const submit = async (): Promise<void> => {
    if (saving.value || !hasAnything.value) {
        return;
    }

    saving.value = true;

    try {
        await axios.post(endpoint.value, {
            answers: buildAnswers(),
            free_text: free() === "" ? null : free(),
        });
        dismissed.value = true;
        emit("submitted");
    } catch (error) {
        const response =
            axios.isAxiosError(error) === true ? error.response : undefined;
        const message = response?.data?.message ?? null;

        // 409 means the offer is spent for good, so the card can only fail again.
        if (response?.status === CONFLICT) {
            dismissed.value = true;
            emit("submitted");
        }

        create({
            title: t("vue.ai_chat_questions_failed"),
            text: typeof message === "string" ? message : "",
            type: "error",
        });
    } finally {
        saving.value = false;
    }
};
</script>
